import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ElectronicSignatureSettings from "./ElectronicInvoice/ElectronicSignatureSettings";
import EmissionAndSequenceSettings from "./ElectronicInvoice/EmissionAndSequenceSettings";
import { useAuth } from "../../context/AuthContext";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

const ENDPOINTS = {
  estado: "/api/facturacion/config/estado/",
  updateLimit: "/api/facturacion/config/document-limits/update/",
};

const ElectronicInvoice = ({ canEditDocumentLimit = false }) => {
  const { accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [loading, setLoading] = useState(true);
  const [firmaEstado, setFirmaEstado] = useState({});
  const [documentos, setDocumentos] = useState({
    limite: null,
    usados: 0,
    disponibles: null,
    ilimitado: true,
  });
  const [establecimientos, setEstablecimientos] = useState([]);
  const [puntos, setPuntos] = useState([]);
  const [secuenciales, setSecuenciales] = useState([]);

  const [editingLimit, setEditingLimit] = useState(false);
  const [docUnlimited, setDocUnlimited] = useState(true);
  const [limitInput, setLimitInput] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  const notify = useCallback(
    (message, severity = "success") => {
      if (typeof alert?.showAlert === "function") {
        alert.showAlert(message, severity);
      }
    },
    [alert]
  );

  const getToken = useCallback(() => {
    return accessToken || localStorage.getItem("accessToken") || localStorage.getItem("access") || "";
  }, [accessToken]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const token = getToken();

      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return fetch(url, {
        ...options,
        headers,
      });
    },
    [getToken]
  );

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);

      const res = await authFetch(ENDPOINTS.estado);
      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo cargar la configuración de facturación.", "error");
        return;
      }

      setFirmaEstado(data.firma || {});
      setDocumentos(data.documentos || {
        limite: null,
        usados: 0,
        disponibles: null,
        ilimitado: true,
      });
      setEstablecimientos(Array.isArray(data.establecimientos) ? data.establecimientos : []);
      setPuntos(Array.isArray(data.puntos) ? data.puntos : []);
      setSecuenciales(Array.isArray(data.secuenciales) ? data.secuenciales : []);

      const limite = data?.documentos?.limite;
      const ilimitado = !!data?.documentos?.ilimitado;

      setDocUnlimited(ilimitado);
      setLimitInput(limite === null || limite === undefined ? "" : String(limite));
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, logout, notify]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const saveLimit = async () => {
    if (!canEditDocumentLimit) return;

    try {
      setSavingLimit(true);

      const payload = {
        limite: docUnlimited ? "ilimitado" : limitInput,
      };

      const res = await authFetch(ENDPOINTS.updateLimit, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (!res.ok || !data.ok) {
        notify(data?.error || "No se pudo actualizar el límite.", "error");
        return;
      }

      await refreshAll();
      setEditingLimit(false);
      notify(data?.message || "Límite actualizado correctamente.", "success");
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSavingLimit(false);
    }
  };

  const fieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        bgcolor: dashboardTheme.card_background,
        color: dashboardTheme.text_color,
        "& fieldset": {
          borderColor: dashboardTheme.border_color,
        },
        "&:hover fieldset": {
          borderColor: dashboardTheme.primary_color,
        },
        "&.Mui-focused fieldset": {
          borderColor: dashboardTheme.primary_color,
        },
      },
      "& .MuiInputLabel-root": {
        color: dashboardTheme.muted_text_color,
        "&.Mui-focused": {
          color: dashboardTheme.primary_color,
        },
      },
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: dashboardTheme.muted_text_color,
      },
    }),
    [dashboardTheme]
  );

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      bgcolor: dashboardTheme.border_color,
      color: dashboardTheme.muted_text_color,
    },
  };

  const outlinedButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.primary_color,
    borderColor: dashboardTheme.border_color,
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor:
        dashboardTheme.theme_mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : `${dashboardTheme.primary_color}10`,
    },
  };

  const cardSx = {
    borderRadius: "14px",
    p: 2,
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow:
      dashboardTheme.theme_mode === "dark"
        ? "none"
        : "0 4px 10px rgba(0,0,0,0.08)",
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={cardSx}>
        <Stack
          spacing={1.2}
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: 260 }}
        >
          <CircularProgress size={30} sx={{ color: dashboardTheme.primary_color }} />
          <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
            Cargando configuración de facturación...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: { xs: 18, md: 20 },
          fontWeight: 600,
          color: dashboardTheme.text_color,
          mb: 2,
        }}
      >
        Facturación Electrónica
      </Typography>

      <Stack spacing={2.2}>
        <Paper elevation={0} sx={cardSx}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                      dashboardTheme.theme_mode === "dark"
                        ? "rgba(255,255,255,0.06)"
                        : `${dashboardTheme.primary_color}14`,
                  }}
                >
                  <ReceiptLongRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: dashboardTheme.muted_text_color,
                      fontWeight: 600,
                    }}
                  >
                    Documentos electrónicos
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 16,
                      color: dashboardTheme.primary_color,
                      fontWeight: 700,
                    }}
                  >
                    {documentos.usados || 0} / {documentos.ilimitado ? "Ilimitado" : documentos.limite}
                  </Typography>
                </Box>
              </Box>

              {canEditDocumentLimit && (
                <>
                  {!editingLimit ? (
                    <Button
                      variant="contained"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => {
                        setDocUnlimited(!!documentos.ilimitado);
                        setLimitInput(documentos.limite === null || documentos.limite === undefined ? "" : String(documentos.limite));
                        setEditingLimit(true);
                      }}
                      sx={primaryButtonSx}
                    >
                      Editar límite
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={saveLimit}
                      disabled={savingLimit}
                      sx={primaryButtonSx}
                    >
                      {savingLimit ? "Guardando..." : "Guardar límite"}
                    </Button>
                  )}
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${dashboardTheme.border_color}`,
                  bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
                }}
              >
                <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
                  Límite
                </Typography>
                <Typography sx={{ fontSize: 17, color: dashboardTheme.text_color, fontWeight: 700 }}>
                  {documentos.ilimitado ? "Ilimitado" : documentos.limite}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${dashboardTheme.border_color}`,
                  bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
                }}
              >
                <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
                  Consumidos
                </Typography>
                <Typography sx={{ fontSize: 17, color: dashboardTheme.text_color, fontWeight: 700 }}>
                  {documentos.usados || 0}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${dashboardTheme.border_color}`,
                  bgcolor: dashboardTheme.table_background || dashboardTheme.card_background,
                }}
              >
                <Typography sx={{ fontSize: 13, color: dashboardTheme.muted_text_color, fontWeight: 600 }}>
                  Disponible
                </Typography>
                <Typography sx={{ fontSize: 17, color: dashboardTheme.text_color, fontWeight: 700 }}>
                  {documentos.ilimitado ? "Ilimitado" : documentos.disponibles}
                </Typography>
              </Paper>
            </Box>

            {editingLimit && (
              <Stack spacing={1.4}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                  <TextField
                    size="small"
                    label="Límite de documentos"
                    disabled={docUnlimited || savingLimit}
                    value={limitInput}
                    onChange={(event) => setLimitInput(event.target.value.replace(/\D/g, ""))}
                    sx={{ ...fieldSx, width: { xs: "100%", sm: 240 } }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={docUnlimited}
                        onChange={(event) => setDocUnlimited(event.target.checked)}
                        disabled={savingLimit}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: dashboardTheme.primary_color,
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            bgcolor: dashboardTheme.primary_color,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14 }}>
                        Ilimitado
                      </Typography>
                    }
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={saveLimit} disabled={savingLimit} sx={primaryButtonSx}>
                    {savingLimit ? "Guardando..." : "Guardar"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => setEditingLimit(false)}
                    disabled={savingLimit}
                    sx={outlinedButtonSx}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>

        <ElectronicSignatureSettings
          firmaEstado={firmaEstado}
          onRefresh={refreshAll}
          authFetch={authFetch}
          parseJsonSafe={parseJsonSafe}
          notify={notify}
          logout={logout}
          fieldSx={fieldSx}
          primaryButtonSx={primaryButtonSx}
          outlinedButtonSx={outlinedButtonSx}
        />

        <EmissionAndSequenceSettings
          establecimientos={establecimientos}
          puntos={puntos}
          secuenciales={secuenciales}
          onRefresh={refreshAll}
          authFetch={authFetch}
          parseJsonSafe={parseJsonSafe}
          notify={notify}
          logout={logout}
          fieldSx={fieldSx}
          primaryButtonSx={primaryButtonSx}
          outlinedButtonSx={outlinedButtonSx}
        />
      </Stack>
    </Box>
  );
};

export default ElectronicInvoice;